// @ts-nocheck
import { useState, useEffect, useRef } from "react";
import { Input, App, Spin, Button, Typography } from "../components/admin-ui";
import { useTranslation } from "react-i18next";
import { useAdminSettings, useUpdateSettings } from "../api/hooks";
import { AdminShell } from "../layouts/AdminShell";

const { Text } = Typography;

/**
 * Configures the customer-app-only per-item pricing markup: items priced at
 * or below the threshold get a flat fee added; items above it get a
 * percentage surcharge instead. Applies to online delivery and customer-app
 * self-pickup orders — never to POS walk-in orders (waived entirely there),
 * and never displayed in stall-app/pos-app (they show the plain menu price).
 */
export function PricingRulesScreen() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { data, isLoading } = useAdminSettings();
  const update = useUpdateSettings();

  const [threshold, setThreshold] = useState("15.00");
  const [flatFee, setFlatFee] = useState("0.50");
  const [percent, setPercent] = useState("3.5");
  const [addonPercent, setAddonPercent] = useState("3.5");
  const [saving, setSaving] = useState(false);

  const hydrated = useRef(false);
  const settings = data?.data as Record<string, unknown> | undefined;
  useEffect(() => {
    if (hydrated.current || !settings) return;
    if (settings.price_markup_threshold != null) setThreshold(Number(settings.price_markup_threshold).toFixed(2));
    if (settings.price_markup_flat_fee != null) setFlatFee(Number(settings.price_markup_flat_fee).toFixed(2));
    if (settings.price_markup_percent != null) setPercent(Number((Number(settings.price_markup_percent) * 100).toFixed(4)).toString());
    if (settings.addon_markup_percent != null) setAddonPercent(Number((Number(settings.addon_markup_percent) * 100).toFixed(4)).toString());
    hydrated.current = true;
  }, [settings]);

  const handleSave = async () => {
    const fields: Array<[string, string, number]> = [["价格门槛", threshold, Infinity], ["固定手续费", flatFee, Infinity], ["百分比手续费", percent, 100], ["加料加价率", addonPercent, 100]];
    for (const [label, raw, max] of fields) {
      const n = Number(raw);
      if (raw.trim() === "" || !Number.isFinite(n)) { void message.error(`${label}必填，请输入数字`); return; }
      if (n < 0) { void message.error(`${label}不能是负数`); return; }
      if (n > max) { void message.error(`${label}不能超过 ${max}%`); return; }
    }
    try {
      setSaving(true);
      await update.mutateAsync({
        price_markup_threshold: parseFloat(threshold) || 0,
        price_markup_flat_fee: parseFloat(flatFee) || 0,
        price_markup_percent: (parseFloat(percent) || 0) / 100,
        addon_markup_percent: (parseFloat(addonPercent) || 0) / 100,
      });
      void message.success(t("pricingRules.saveSuccess", "Pricing rules saved"));
    } catch {
      void message.error(t("pricingRules.saveFailed", "Failed to save pricing rules"));
    } finally {
      setSaving(false);
    }
  };

  const exampleLow = 12.0;
  const exampleHigh = 16.0;
  // Same rule as the backend (PricingService.applyCustomerMarkup): marked-up price is always rounded UP to 10 sen.
  // The rule is ONE side: the customer price carries two of it, the stall is deducted one (backend: customerSideRule / stallDeductionFor).
  const th = parseFloat(threshold) || 0, flat = parseFloat(flatFee) || 0, pct = (parseFloat(percent) || 0) / 100;
  const markUp = (p: number) => {
    const raw = p <= th ? p + 2 * flat : p + p * 2 * pct;
    return Math.ceil(raw * 10 - 1e-9) / 10;  // 1e-9 guards float noise, e.g. 21.4000000000000004
  };
  const lowResult = markUp(exampleLow);
  const highResult = markUp(exampleHigh);
  const stallCut = (p: number) => (p <= th ? flat : Math.round(p * pct * 100 + 1e-9) / 100);   // what the stall is deducted
  const platformTake = (p: number, result: number) => (result - p) + stallCut(p);                // customer markup + stall deduction

  // Add-ons: cost + percent, rounded UP to 10 sen (PricingService.applyAddonMarkup). Example uses a RM1.00 add-on.
  const addonStallCut = Math.round((parseFloat(addonPercent) || 0) + 1e-9) / 100;
  const addonFee = Math.ceil((1 + 2 * (parseFloat(addonPercent) || 0) / 100) * 10 - 1e-9) / 10 - 1;

  return (
    <AdminShell>
      <div className="admin-pricing-page admin-list-page">
        <div className="admin-pricing-inner">
          <div className="admin-pricing-toolbar">
            <div>
            <h1 className="admin-page-title">
              {t("pricingRules.title", "Pricing Rules")}
            </h1>
            <p className="admin-pricing-subtitle">
              {t("pricingRules.subtitle", "Per-item markup shown to customer-app orders (delivery + self-pickup). Walk-in POS orders always use the original menu price.")}
            </p>
            </div>
          </div>

          {isLoading ? (
            <div className="admin-settings-loading"><Spin /></div>
          ) : (
            <div className="admin-pricing-layout">
              <section className="admin-pricing-form-card admin-settings-card">
                <div className="admin-settings-card-heading">
                  <div>
                    <h2>{t("pricingRules.configurationTitle")}</h2>
                    <p>{t("pricingRules.configurationSubtitle")}</p>
                  </div>
                </div>

                <div className="admin-pricing-fields">
              <div className="admin-settings-field admin-pricing-threshold-field">
                <label>
                  {t("pricingRules.thresholdLabel", "Price threshold (RM)")}
                </label>
                <Input value={threshold} onChange={(e) => setThreshold(e.target.value)} style={{ height: 44 }} />
                <Text type="secondary" className="admin-settings-field-hint">
                  {t("pricingRules.thresholdHint", "Items priced at or below this amount get the flat fee. Items above it get the percentage instead.")}
                </Text>
              </div>

              <div className="admin-settings-field">
                <label>
                  {t("pricingRules.flatFeeLabel", "Flat fee for items ≤ threshold (RM)")}
                </label>
                <Input value={flatFee} onChange={(e) => setFlatFee(e.target.value)} style={{ height: 44 }} />
              </div>

              <div className="admin-settings-field">
                <label>
                  {t("pricingRules.percentLabel", "Surcharge for items > threshold (%)")}
                </label>
                <Input value={percent} onChange={(e) => setPercent(e.target.value)} style={{ height: 44 }} />
              </div>
                </div>

              <div className="admin-pricing-addon-section">
                <div className="admin-pricing-section-copy">
                <h3>
                  {t("pricingRules.addonSectionTitle", "Add-on markup rate")}
                </h3>
                <p>
                  {t("pricingRules.addonSectionSub", "Applied to every paid add-on's own cost individually, regardless of which branch the base item's price took above. Independent from the base-item rule.")}
                </p>
                </div>
                <div className="admin-settings-field">
                <label>
                  {t("pricingRules.addonPercentLabel", "Add-on markup (%)")}
                </label>
                <Input value={addonPercent} onChange={(e) => setAddonPercent(e.target.value)} style={{ height: 44 }} />
                </div>
              </div>

              <div className="admin-settings-card-footer">
                <span>{t("pricingRules.saveHint")}</span>
                <Button className="admin-pricing-save" type="primary" loading={saving} onClick={() => void handleSave()}>
                  {t("common.save", "Save")}
                </Button>
              </div>
              </section>

              <aside className="admin-pricing-example admin-settings-card">
                <div className="admin-settings-card-heading">
                  <div>
                    <h2>
                  {t("pricingRules.exampleTitle", "Live example")}
                    </h2>
                    <p>{t("pricingRules.exampleSubtitle")}</p>
                  </div>
                </div>
                <div className="admin-pricing-example-list">
                <div className="admin-pricing-example-row">
                  <span className="admin-pricing-example-index">01</span>
                  <p>
                  {t("pricingRules.exampleLow", "RM {{price}} item → customer sees RM {{result}} (≤ threshold, flat fee)", {
                    price: exampleLow.toFixed(2), result: lowResult.toFixed(2),
                  })}
                  <small className="admin-pricing-example-stall">
                    {t("pricingRules.exampleStallNet", "Stall est. revenue RM {{net}} (deducted RM {{fee}}; platform takes RM {{platform}} in total)", {
                      net: (exampleLow - stallCut(exampleLow)).toFixed(2), fee: stallCut(exampleLow).toFixed(2), platform: platformTake(exampleLow, lowResult).toFixed(2),
                    })}
                  </small>
                  </p>
                </div>
                <div className="admin-pricing-example-row">
                  <span className="admin-pricing-example-index">02</span>
                  <p>
                  {t("pricingRules.exampleHigh", "RM {{price}} item → customer sees RM {{result}} (> threshold, {{percent}}% surcharge)", {
                    price: exampleHigh.toFixed(2), result: highResult.toFixed(2), percent: (parseFloat(percent) || 0) * 2,
                  })}
                  <small className="admin-pricing-example-stall">
                    {t("pricingRules.exampleStallNet", "Stall est. revenue RM {{net}} (deducted RM {{fee}}; platform takes RM {{platform}} in total)", {
                      net: (exampleHigh - stallCut(exampleHigh)).toFixed(2), fee: stallCut(exampleHigh).toFixed(2), platform: platformTake(exampleHigh, highResult).toFixed(2),
                    })}
                  </small>
                  </p>
                </div>
                <div className="admin-pricing-example-row">
                  <span className="admin-pricing-example-index">03</span>
                  <p>
                  {t("pricingRules.exampleAddon", "RM {{price}} add-on → customer sees RM {{result}} ({{percent}}% add-on markup, always applied)", {
                    price: (1).toFixed(2), result: (1 + addonFee).toFixed(2), percent: (parseFloat(addonPercent) || 0) * 2,
                  })}
                  <small className="admin-pricing-example-stall">
                    {t("pricingRules.exampleStallNet", "Stall est. revenue RM {{net}} (deducted RM {{fee}}; platform takes RM {{platform}} in total)", {
                      net: (1 - addonStallCut).toFixed(2), fee: addonStallCut.toFixed(2), platform: (addonFee + addonStallCut).toFixed(2),
                    })}
                  </small>
                  </p>
                </div>
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
